import {LitElement, css, html} from "lit";
import {LANG_DEF} from "./profile-finance-grid-balance-sheet.js";

export class ProfileFinanceBalanceSheetChart extends LitElement {

	static get properties() {
		return {
			lang: { type: String, reflect: true },
			profileID: { type: String, attribute: "profile-id", reflect: true },
			loading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			year: { type: Number, attribute: false },
		};
	}

	get ChartContainer() {
		if(!this._chartContainer)
			this._chartContainer = this.shadowRoot.querySelector("#ChartContainer");
		return this._chartContainer;
	}

	constructor() {
		super();

		this.lang = "en";

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		/**
		 * The initial year.
		 *
		 * The columns and rows render depending on this value.
		 *
		 * The columns are composed out of this value: (year - 2) & (year) & (year + 2) which are 5 years in total, with
		 * the current year in the center.
		 *
		 * @type {number}
		 */
		this.year = new Date().getFullYear();

		this.chart = null;
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.refresh());
				break;
		}
	}

	reset() {
		this.loading = false;
		this.error = null;
		this.year = new Date().getFullYear();
	}

	refresh() {
		this.load(this.year - 1);
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(fromYear, toYear = null) {
		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const query = `
			query ProfileBalanceSheetValues($profileID: ID!, $fromYear: Int!, $toYear: Int, $first: Int, $after: String) {
				listBalanceSheet(first: $first, after: $after) {
					edges {
						node {
							ID
							Name
							Type
							ProfileValues(profileID: $profileID, fromYear: $fromYear, toYear: $toYear, fillYears: true) {
								Year
								Value {Amount}
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`;

		const variables = {
			profileID: this.profileID,
			first: 10,
			fromYear: fromYear,
			toYear: toYear,
			fillYears: true
		};

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: query,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			const chartData = {
				Series: [],
			};

			/*const format = [
				{
					data: [{name: "Activa A1", y: 1}, {name: "Passiva P1", y: 3}]
				},
				{
					data: [{name: "Activa A2", y: 2}, {name: "Passiva P2", y: 2}]
				},
				{
					data: [{name: "Activa A3", y: 3}, {name: "Passiva P3", y: 1}]
				}
			];*/

			const edges = response.data.listBalanceSheet.edges;
			const activaData = edges.filter(item => item.node.Type.startsWith("ACTIVA_"));
			const passivaData = edges.filter(item => item.node.Type.startsWith("PASSIVA_"));

			activaData.forEach((item, i) => {
				const balanceSheet = item.node;

				if(!chartData.Series[i])
					chartData.Series[i] = { data: [null, null] };

				if(balanceSheet.ProfileValues[0].Value !== null) {
					chartData.Series[i].data[0] = {
						name: balanceSheet.Name,
						y: parseFloat(balanceSheet.ProfileValues[0].Value.Amount)
					};
				}
			});

			passivaData.forEach((item, i) => {
				const balanceSheet = item.node;

				if(!chartData.Series[i])
					chartData.Series[i] = { data: [null, null] };

				if(balanceSheet.ProfileValues[0].Value !== null) {
					chartData.Series[i].data[1] = {
						name: balanceSheet.Name,
						y: parseFloat(balanceSheet.ProfileValues[0].Value.Amount)
					};
				}
			});

			if(this.chart !== null)
				this.updateChart(chartData);
			else
				this.buildChart(chartData);
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	updateChart(data) {
		this.chart.update({ series: data.Series });
	}

	buildChart(data) {
		this.chart = Highcharts.chart(this.ChartContainer, {
			chart: {
				type: "column",
				height: 130
			},

			title: {
				text: ""
			},

			subtitle: {
				text: ""
			},

			xAxis: {
				categories: [LANG_DEF[this.lang]["activa"], LANG_DEF[this.lang]["passiva"]]
			},

			yAxis: {
				title: {
					text: ''
				},
				stackLabels: {
					enabled: true
				}
			},

			legend: {
				enabled: false
			},

			credits: {
				enabled: false
			},

			tooltip: {
				headerFormat: '{point.x}<br>',
				pointFormat: '<b>{point.name}</b>: {point.y}'
			},

			plotOptions: {
				column: {
					stacking: "normal"
				},
				series: {
					states: {
						inactive: {
							opacity: 1
						}
					}
				}
			},

			colors: [
				"var(--aumentoo-highcharts-color0)",
				"var(--aumentoo-highcharts-color1)",
				"var(--aumentoo-highcharts-color2)",
				"var(--aumentoo-highcharts-color3)",
				"var(--aumentoo-highcharts-color4)",
			],

			series: data.Series,
		});
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				border-radius: 8px;
			}
		`;
	}

	render() {
		return html`<div id="ChartContainer" class="container"></div>`;
	}

}

customElements.define("profile-finance-balance-sheet-chart", ProfileFinanceBalanceSheetChart);